import React, { useMemo } from 'react';
import { Textarea } from '@/shared/components/ui/textarea';
import { cn } from '@/shared/lib/utils';
import { detectRedWords } from '@/shared/utils/redWords';

interface RedWordTextareaProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  shouldHighlight: boolean; // Whether to show red word highlighting
}

const RedWordTextarea: React.FC<RedWordTextareaProps> = ({
  value,
  onChange,
  placeholder,
  className,
  style,
  id,
  textareaRef,
  shouldHighlight,
}) => {
  const redWordMatches = useMemo(() => {
    if (!shouldHighlight) return [];
    return detectRedWords(value);
  }, [value, shouldHighlight]);

  const hasRedWords = redWordMatches.length > 0;

  return (
    <>
      <style>{`
        .red-word-textarea-container {
          position: relative;
          width: 100%;
        }
        
        .red-word-textarea-with-highlights {
          position: relative;
        }
        
        .red-word-textarea-with-highlights.has-red-words {
          background: linear-gradient(to right, 
            transparent 0%, 
            rgba(254, 226, 226, 0.3) 0%, 
            rgba(254, 226, 226, 0.3) 100%, 
            transparent 100%
          );
        }
        
        .dark .red-word-textarea-with-highlights.has-red-words {
          background: linear-gradient(to right, 
            transparent 0%, 
            rgba(220, 38, 38, 0.15) 0%, 
            rgba(220, 38, 38, 0.15) 100%, 
            transparent 100%
          );
        }
        
        .red-word-warning {
          position: absolute;
          bottom: -25px;
          left: 0;
          font-size: 12px;
          color: #dc2626;
          background: #fee2e2;
          padding: 2px 8px;
          border-radius: 4px;
          border: 1px solid #fecaca;
          z-index: 10;
          pointer-events: none;
        }
        
        .dark .red-word-warning {
          color: #fca5a5;
          background: rgba(220, 38, 38, 0.2);
          border-color: rgba(220, 38, 38, 0.3);
        }
      `}</style>
      
      <div className="red-word-textarea-container">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          spellCheck={true}
          className={cn(
            'red-word-textarea-with-highlights',
            shouldHighlight && hasRedWords && 'has-red-words',
            className
          )}
          style={style}
          id={id}
        />
        {shouldHighlight && hasRedWords && (
          <div className="red-word-warning">
            {redWordMatches.length} red word{redWordMatches.length !== 1 ? 's' : ''} detected
          </div>
        )}
      </div>
    </>
  );
};

export default RedWordTextarea;